import React from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { CURRENCY } from '../constants';

const CartDrawer: React.FC = () => {
  const {
    isCartOpen,
    toggleCart,
    cart,
    removeFromCart,
    updateCartQuantity,
    cartTotal
  } = useShop();
  const navigate = useNavigate();

  const handleCheckout = () => {
    toggleCart();
    navigate('/checkout');
  };

  // Animation State
  const [shouldRender, setShouldRender] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isCartOpen) {
      setShouldRender(true);
      // Wait a frame for mount to occur, then trigger animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false); // Start exit animation
      // Wait for animation to finish before unmounting
      timeoutId = setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }

    return () => clearTimeout(timeoutId);
  }, [isCartOpen]);

  if (!shouldRender) return null;

  return (
    <div className={`fixed inset-0 z-50 overflow-hidden ${isVisible ? 'pointer-events-auto' : 'pointer-events-none'}`}>
      <div
        className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        onClick={toggleCart}
      />

      <div className="absolute inset-y-0 right-0 max-w-md w-full flex pointer-events-none justify-end">
        <div
          className={`h-full w-[85vw] sm:w-full bg-mauve-base shadow-2xl flex flex-col transition-transform duration-300 ease-in-out pointer-events-auto ${isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
        >
          {/* Header */}
          <div className="px-6 py-4 bg-white/50 border-b border-mauve-medium/20 flex items-center justify-between">
            <h2 className="text-xl font-serif font-bold text-mauve-dark flex items-center">
              <Icon icon="lucide:shopping-bag" className="mr-2" width="20" />
              Your Bag
            </h2>
            <button
              onClick={toggleCart}
              className="p-2 text-gray-400 hover:text-mauve-dark transition-colors rounded-full hover:bg-mauve-light/20"
              aria-label="Close cart"
            >
              <Icon icon="lucide:x" width="24" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                <Icon icon="lucide:shopping-bag" width="64" className="text-mauve-medium/30" />
                <p className="text-lg text-mauve-text/60 font-medium">Your bag is empty</p>
                <button
                  onClick={toggleCart}
                  className="mt-4 px-6 py-2 bg-mauve-dark text-white rounded-full hover:bg-mauve-medium transition-colors"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.id} className="flex gap-4 bg-white p-3 rounded-lg shadow-sm border border-mauve-light/20">
                  <div className="h-24 w-20 flex-shrink-0 overflow-hidden rounded-md border border-mauve-light/20">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>

                  <div className="flex flex-1 flex-col justify-between min-w-0">
                    <div>
                      <div className="flex justify-between items-start text-base font-medium text-mauve-text">
                        <h3 className="font-serif line-clamp-1 mr-2">{item.name}</h3>
                        <p className="whitespace-nowrap">{CURRENCY}{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-mauve-medium truncate">{item.category}</p>
                        {item.selectedSize && (
                          <span className="text-xs font-medium bg-mauve-base px-2 py-0.5 rounded text-mauve-dark whitespace-nowrap">
                            Size: {item.selectedSize.name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-mauve-medium/30 rounded-md">
                        <button
                          onClick={() => updateCartQuantity(item.id, item.quantity - 1, item.selectedSize?.name)}
                          className="p-1 hover:bg-mauve-light/20 text-mauve-dark transition-colors"
                          disabled={item.quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          <Icon icon="lucide:minus" width="14" />
                        </button>
                        <span className="px-3 font-medium text-mauve-text text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateCartQuantity(item.id, item.quantity + 1, item.selectedSize?.name)}
                          className="p-1 hover:bg-mauve-light/20 text-mauve-dark transition-colors"
                          disabled={item.quantity >= (item.selectedSize ? item.selectedSize.stock : item.stock)}
                          aria-label="Increase quantity"
                        >
                          <Icon icon="lucide:plus" width="14" />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFromCart(item.id, item.selectedSize?.name)}
                        className="font-medium text-red-400 hover:text-red-500 text-xs uppercase tracking-wide ml-2 bg-red-50 px-2 py-1 rounded transition-colors"
                        aria-label={`Remove ${item.name} from cart`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {cart.length > 0 && (
            <div className="border-t border-mauve-medium/20 px-6 py-6 bg-white/50">
              <div className="flex justify-between text-base font-medium text-mauve-text mb-4">
                <p>Subtotal</p>
                <p>{CURRENCY}{cartTotal.toLocaleString()}</p>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full flex items-center justify-center rounded-full border border-transparent bg-mauve-dark px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-mauve-medium transition-all hover:shadow-md"
              >
                Checkout <Icon icon="lucide:arrow-right" width="18" className="ml-2" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default CartDrawer;